'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface MenuItem {
  label: string;
  path: string;
  icon: string;
  badge?: number;
  requiredRole?: string[];
}

interface MenuSection {
  title: string;
  items: MenuItem[];
  requiredRole?: string[];
}

const Sidebar = ({ isOpen = true, onClose }: SidebarProps) => {
  const pathname = usePathname();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const menuSections: MenuSection[] = [
    {
      title: 'Core Operations',
      items: [
        {
          label: 'Dashboard',
          path: '/main-dashboard',
          icon: 'HomeIcon',
        },
        {
          label: 'Process Management',
          path: '/process-management',
          icon: 'RectangleStackIcon',
        },
        {
          label: 'Task Center',
          path: '/task-management-center',
          icon: 'ClipboardDocumentCheckIcon',
          badge: 5,
        },
      ],
    },
    {
      title: 'Process Development',
      items: [
        {
          label: 'Process Wizard',
          path: '/process-creation-wizard',
          icon: 'SparklesIcon',
        },
        {
          label: 'Form Builder',
          path: '/dynamic-form-builder',
          icon: 'DocumentTextIcon',
        },
        {
          label: 'Template Gallery',
          path: '/template-gallery',
          icon: 'RectangleGroupIcon',
        },
        {
          label: 'My Templates',
          path: '/personal-template-manager',
          icon: 'FolderIcon',
        },
      ],
    },
    {
      title: 'Customization',
      items: [
        {
          label: 'Portal Builder',
          path: '/portal-builder',
          icon: 'PaintBrushIcon',
        },
        {
          label: 'Portal View',
          path: '/custom-portal-view',
          icon: 'WindowIcon',
        },
      ],
    },
    {
      title: 'Analytics',
      items: [
        {
          label: 'Analytics Dashboard',
          path: '/analytics-dashboard',
          icon: 'ChartBarIcon',
        },
      ],
    },
    {
      title: 'Administration',
      items: [
        {
          label: 'User Management',
          path: '/user-administration',
          icon: 'UsersIcon',
          requiredRole: ['Administrator'],
        },
        {
          label: 'Delegation Control',
          path: '/delegation-control-center',
          icon: 'ArrowsRightLeftIcon',
          requiredRole: ['Administrator', 'Manager'],
        },
      ],
      requiredRole: ['Administrator', 'Manager'],
    },
  ];

  const isActive = (path: string) => pathname === path;

  return (
    <>
      {isOpen && (
        <div data-component-id="src/components/common/Sidebar.tsx:128:8" data-component-path="src/components/common/Sidebar.tsx" data-component-line="128" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20fixed%20inset-0%20bg-foreground%2F50%20z-40%22%7D"
          className="lg:hidden fixed inset-0 bg-foreground/50 z-40"
          onClick={onClose}
        />
      )}

      <aside data-component-id="src/components/common/Sidebar.tsx:134:6" data-component-path="src/components/common/Sidebar.tsx" data-component-line="134" data-component-file="Sidebar.tsx" data-component-name="aside" data-component-content="%7B%22elementName%22%3A%22aside%22%7D"
        className={`fixed left-0 top-16 bottom-0 bg-surface border-r border-border z-45 transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'w-20' : 'w-72'}`}
      >
        <div data-component-id="src/components/common/Sidebar.tsx:139:8" data-component-path="src/components/common/Sidebar.tsx" data-component-line="139" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20flex%20flex-col%22%7D" className="h-full flex flex-col">
          <div data-component-id="src/components/common/Sidebar.tsx:140:10" data-component-path="src/components/common/Sidebar.tsx" data-component-line="140" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20py-6%20px-4%22%7D" className="flex-1 overflow-y-auto py-6 px-4">
            <nav data-component-id="src/components/common/Sidebar.tsx:141:12" data-component-path="src/components/common/Sidebar.tsx" data-component-line="141" data-component-file="Sidebar.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
              {menuSections.map((section, sectionIndex) => (
                <div data-component-id="src/components/common/Sidebar.tsx:143:16" data-component-path="src/components/common/Sidebar.tsx" data-component-line="143" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={sectionIndex}>
                  {!isCollapsed && (
                    <h3 data-component-id="src/components/common/Sidebar.tsx:145:20" data-component-path="src/components/common/Sidebar.tsx" data-component-line="145" data-component-file="Sidebar.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22px-3%20mb-2%20text-xs%20font-semibold%20text-muted-foreground%20uppercase%20tracking-wider%22%7D" className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {section.title}
                    </h3>
                  )}
                  <ul data-component-id="src/components/common/Sidebar.tsx:149:18" data-component-path="src/components/common/Sidebar.tsx" data-component-line="149" data-component-file="Sidebar.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-1%22%7D" className="space-y-1">
                    {section.items.map((item, itemIndex) => (
                      <li data-component-id="src/components/common/Sidebar.tsx:151:22" data-component-path="src/components/common/Sidebar.tsx" data-component-line="151" data-component-file="Sidebar.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={itemIndex}>
                        <Link data-component-id="src/components/common/Sidebar.tsx:152:24" data-component-path="src/components/common/Sidebar.tsx" data-component-line="152" data-component-file="Sidebar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                          href={item.path}
                          onClick={onClose}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md transition-micro group ${
                            isActive(item.path)
                              ? 'bg-primary text-primary-foreground'
                              : 'text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon data-component-id="src/components/common/Sidebar.tsx:161:26" data-component-path="src/components/common/Sidebar.tsx" data-component-line="161" data-component-file="Sidebar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                            name={item.icon as any}
                            size={20}
                            className={`flex-shrink-0 ${
                              isActive(item.path)
                                ? 'text-primary-foreground'
                                : 'text-muted-foreground group-hover:text-foreground'
                            }`}
                          />
                          {!isCollapsed && (
                            <>
                              <span data-component-id="src/components/common/Sidebar.tsx:172:30" data-component-path="src/components/common/Sidebar.tsx" data-component-line="172" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex-1%20text-sm%20font-medium%22%7D" className="flex-1 text-sm font-medium">
                                {item.label}
                              </span>
                              {item.badge && item.badge > 0 && (
                                <span data-component-id="src/components/common/Sidebar.tsx:176:32" data-component-path="src/components/common/Sidebar.tsx" data-component-line="176" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-2%20py-0.5%20text-xs%20font-medium%20bg-accent%20text-accent-foreground%20rounded-full%22%7D" className="px-2 py-0.5 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                                  {item.badge}
                                </span>
                              )}
                            </>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>

          <div data-component-id="src/components/common/Sidebar.tsx:191:10" data-component-path="src/components/common/Sidebar.tsx" data-component-line="191" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-border%20p-4%22%7D" className="border-t border-border p-4">
            <button data-component-id="src/components/common/Sidebar.tsx:192:12" data-component-path="src/components/common/Sidebar.tsx" data-component-line="192" data-component-file="Sidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20justify-center%20w-full%20p-2%20hover%3Abg-muted%20rounded-md%20transition-micro%22%7D"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex items-center justify-center w-full p-2 hover:bg-muted rounded-md transition-micro"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon data-component-id="src/components/common/Sidebar.tsx:197:14" data-component-path="src/components/common/Sidebar.tsx" data-component-line="197" data-component-file="Sidebar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-muted-foreground%22%7D"
                name={isCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
                size={20}
                className="text-muted-foreground"
              />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;